import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  BaseAvatarSize,
  BaseButtonSize,
  BaseCoreState,
  baseFilesizeValidator,
  baseFiletypeValidator,
  BaseInquiryDocumentToUploadInterface,
  baseInquirySelectors,
  BaseModalService,
  DocumentFormInterface,
  InquiryDocumentResponse,
  ProfileDocumentResponse,
  BaseInquiryDocumentUploadService,
} from 'base';

@Component({
  selector: 'base-customer-inquiry-document',
  templateUrl: './base-customer-inquiry-document.component.html',
})
export class BaseCustomerInquiryDocumentComponent implements OnInit, OnDestroy {
  @ViewChild('inquiryDocumentModal') inquiryDocumentModal: ElementRef;
  @Input() inquiryId?: number;

  documentForm: FormGroup<DocumentFormInterface>;
  isDocumentFormSubmitted = false;
  inquiryDocumentInfo?: InquiryDocumentResponse;
  documentToUploadInfo: BaseInquiryDocumentToUploadInterface | null = null;
  isExistingDocument: boolean;

  buttonSize = BaseButtonSize;
  avatarSize = BaseAvatarSize;

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private fb: NonNullableFormBuilder,
    private inquiryDocumentUploadService: BaseInquiryDocumentUploadService
  ) {}

  ngOnInit() {
    this.initDocumentForm();
    this.subscribeToInquiryDocumentInfo();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openUploadInquiryDocumentModal() {
    const modalRef = this.modalService.open(
      {
        headText: 'document.add',
        bodyTemplate: this.inquiryDocumentModal,
        okText: 'action.add',
        canBeClosed: this.canDocumentModalBeClosed.bind(this),
      },
      {
        size: 'lg',
      }
    );

    modalRef.result.then(
      () => {
        if (this.documentForm.valid) {
          const documentFormValue = this.documentForm.getRawValue();

          this.inquiryDocumentUploadService.setDocumentInfo({
            file: documentFormValue.file as File,
            name: documentFormValue.name,
          });

          this.resetDocumentForm();
        }
      },
      () => {
        this.resetDocumentForm();
      }
    );
  }

  initDocumentForm() {
    this.documentForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      category: [null as { label: string; value: number } | null],
      validUntil: [null as string | null],
      private: [false],
      file: this.fb.control<ProfileDocumentResponse | File | null>(null, {
        validators: [baseFilesizeValidator(), baseFiletypeValidator(), Validators.required],
      }),
      id: [''],
    });
  }

  cleanInquiryDocumentToUpload(): void {
    this.inquiryDocumentUploadService.clearDocumentInfo();
  }

  private subscribeToInquiryDocumentInfo(): void {
    combineLatest([
      this.store.select(baseInquirySelectors.getInquiryDocumentInfo),
      this.inquiryDocumentUploadService.documentToUpload$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([inquiryDocumentState, documentToUpload]) => {
        this.documentToUploadInfo = documentToUpload;
        if (!!documentToUpload) {
          this.isExistingDocument = false;
          return;
        }
        if (!!inquiryDocumentState.ok) {
          this.inquiryDocumentInfo = inquiryDocumentState.data as InquiryDocumentResponse;
          this.isExistingDocument = true;
        }
      });
  }

  private resetDocumentForm(): void {
    this.documentForm.reset();
    this.isDocumentFormSubmitted = false;
  }

  private canDocumentModalBeClosed(): boolean {
    this.isDocumentFormSubmitted = true;
    return this.documentForm.valid;
  }
}
